import React, { useState, useEffect } from "react";
import "./PageStyles.css";

const ArticlesPage = () => {
  const [articles, setArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await fetch(`${apiUrl}/articles`);
        if (!response.ok) {
          throw new Error(
            `Failed to fetch: ${response.status} ${response.statusText}`
          );
        }
        const data = await response.json();
        // Handle both direct array and API Gateway "body" string format
        const parsedData = typeof data.body === 'string' ? JSON.parse(data.body) : data;
        setArticles(Array.isArray(parsedData) ? parsedData : []);
      } catch (e) {
        setError(e.message);
        console.error("Failed to fetch articles:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, [apiUrl]);

  const extractArticleContent = (html) => {
    if (!html) return "";
    
    let content = html;
    // Extract content within <body> tags if they exist
    const bodyMatch = content.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    if (bodyMatch) {
      content = bodyMatch[1].trim();
    }

    // Strip the first <h1> match to avoid double titles (frontend already shows the title)
    content = content.replace(/<h1[^>]*>[\s\S]*?<\/h1>/i, "").trim();
    
    return content;
  };

  const handleArticleClick = async (articleId) => {
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/articles/${articleId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch article metadata: ${response.status}`);
      }
      const data = await response.json();
      
      // If we have a content_url (Presigned S3 URL), fetch the actual content
      if (data.content_url) {
        try {
          const contentResponse = await fetch(data.content_url);
          if (!contentResponse.ok) {
            throw new Error(`Failed to fetch content from storage: ${contentResponse.status}`);
          }
          data.content = await contentResponse.text();
        } catch (contentError) {
          console.error("Error fetching content from S3:", contentError);
          data.content = "<p>Error loading article content from storage.</p>";
        }
      }
      
      // Safeguard: Clean content in the frontend
      if (data && data.content) {
        data.content = extractArticleContent(data.content);
      }
      
      setSelectedArticle(data);
    } catch (e) {
      console.error("Failed to fetch single article:", e);
      // Optional: show error toast
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedArticle(null);
  };

  if (loading && !selectedArticle) {
    return (
      <div className="page-container">
        <p>Loading articles...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <p>Error fetching articles: {error}</p>
      </div>
    );
  }

  if (selectedArticle) {
    return (
      <div className="page-container">
        <button onClick={handleBack} className="back-btn">
          &larr; Back to Articles
        </button>
        <article className="full-article">
          <h2>{selectedArticle.title}</h2>
          <span className="article-date">
            Published on: {new Date(selectedArticle.published_at).toLocaleDateString()}
          </span>
          <div
            className="article-content"
            dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
          />
        </article>
      </div>
    );
  }

  return (
    <div className="page-container">
      <h2>Articles</h2>
      <p>
        Here you will find a collection of articles on various computing topics.
      </p>
      <div className="content-grid">
        {articles && articles.length > 0 ? (
          articles.map((article) => (
            <div key={article.article_id} className="content-card">
              <h3>{article.title}</h3>
              <p>{article.summary}</p>
              <span>
                Published on: {new Date(article.published_at).toLocaleDateString()}
              </span>
              <button
                className="read-more-btn"
                onClick={() => handleArticleClick(article.article_id)}
              >
                Read More
              </button>
            </div>
          ))
        ) : (
          <p>No articles found.</p>
        )}
      </div>
    </div>
  );
};

export default ArticlesPage;
